import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Typography, Table, Button, Input, Space, Tag, Tabs, Modal, Form, Tooltip, message } from 'antd';
import { PlusOutlined, EditOutlined, CommentOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { UserContext } from '../App';
import RecordCommentsDrawer from '../components/RecordCommentsDrawer';
import { ClientFields, ContactModal, CLIENT_STATUSES, CONTACT_STATUSES, STATUS_COLOR } from '../components/ClientForms';

// Admin > Clients: the clients we serve and their contacts, on two tabs.
// A client links to campaigns and teams; a contact links to clients, and to
// campaigns and teams picked from those clients' campaigns and teams.
// CanEditClients: 1 View, 2 Edit (both limited to Visible Campaigns by the
// API), 3 Edit Manager.
//
// A client is edited on its own page (/admin/clients/:id): the name opens it,
// and Add Client goes straight there. Contacts, having no page, are edited
// here.

const tagList = (ids, byId) => (
  <Space size={[0, 4]} wrap>
    {(ids || []).map((id) => <Tag key={id}>{byId[id] || `#${id}`}</Tag>)}
  </Space>
);

const byName = (a, b) => (a || '').localeCompare(b || '');

// defaultFilter: the statuses shown until the user changes the filter (the
// Contacts tab opens on Active only).
const statusColumn = (values, defaultFilter) => ({
  title: 'Status', dataIndex: 'Status', key: 'Status', width: 120,
  filters: values.map((v) => ({ text: v, value: v })),
  ...(defaultFilter ? { defaultFilteredValue: defaultFilter } : {}),
  onFilter: (v, r) => r.Status === v,
  sorter: (a, b) => byName(a.Status, b.Status),
  render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag>,
});

const commentsColumn = (onOpen) => ({
  title: 'Comments', dataIndex: 'CommentCount', key: 'CommentCount', width: 110,
  sorter: (a, b) => (a.CommentCount || 0) - (b.CommentCount || 0),
  render: (n, r) => (
    <Button size="small" icon={<CommentOutlined />} onClick={() => onOpen(r)}>{n || 0}</Button>
  ),
});

export default function ClientsPage() {
  const navigate = useNavigate();
  const currentUser = useContext(UserContext);
  const canEdit = (currentUser?.CanEditClients ?? 0) >= 2;
  const [tab,      setTab]      = useState('clients');
  const [clients,  setClients]  = useState([]);
  const [contacts, setContacts] = useState([]);
  const [options,  setOptions]  = useState({ campaigns: [], teams: [], clients: [] });
  const [loading,  setLoading]  = useState(false);
  const [search,   setSearch]   = useState({ clients: '', contacts: '' });

  const [addClientOpen, setAddClientOpen] = useState(false);
  const [contactModal,  setContactModal]  = useState(null);   // null | { contact }
  const [saving, setSaving] = useState(false);
  const [commentsFor, setCommentsFor] = useState(null);    // null | {kind, id, name}
  const [clientForm]  = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const [cl, co, op] = await Promise.all([
        api.get('/api/clients'),
        api.get('/api/client-contacts'),
        api.get('/api/clients/options'),
      ]);
      setClients(cl.data || []);
      setContacts(co.data || []);
      setOptions(op.data || { campaigns: [], teams: [], clients: [] });
    } catch {
      message.error('Failed to load clients.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const campaignById = useMemo(() => Object.fromEntries(options.campaigns.map((c) => [c.ID, c.Name])), [options]);
  const teamById     = useMemo(() => Object.fromEntries(options.teams.map((t) => [t.ID, t.Name])), [options]);
  const clientById   = useMemo(() => Object.fromEntries(clients.map((c) => [c.ID, c.Name])), [clients]);
  const contactById  = useMemo(() => Object.fromEntries(contacts.map((c) => [c.ID, `${c.FirstName} ${c.LastName}`])), [contacts]);

  // A record's campaigns (already limited to Visible Campaigns by the API),
  // for the comment Campaign dropdown, by name.
  const campaignChoices = (ids) => (ids || [])
    .map((id) => ({ value: id, label: campaignById[id] || `#${id}` }))
    .sort((a, b) => byName(a.label, b.label));

  // -- clients ---------------------------------------------------------------

  const openAddClient = () => {
    clientForm.resetFields();
    setAddClientOpen(true);
  };

  // Created, then straight to its page to carry on there (contacts, comments).
  const addClient = async () => {
    let v;
    try { v = await clientForm.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const res = await api.post('/api/clients', v);
      setAddClientOpen(false);
      navigate(`/admin/clients/${res.data.id}`);
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const clientColumns = [
    { title: 'ID', dataIndex: 'ID', key: 'ID', width: 60 },
    { title: 'Name', dataIndex: 'Name', key: 'Name', width: 220, sorter: (a, b) => byName(a.Name, b.Name), defaultSortOrder: 'ascend',
      render: (v, c) => <Link to={`/admin/clients/${c.ID}`}>{v}</Link> },
    // One line here; the whole text on hover and on the client's page.
    { title: 'Contract Details', dataIndex: 'ContractDetails', key: 'ContractDetails', width: 320, ellipsis: { showTitle: false },
      render: (v) => (v
        ? <Tooltip title={<div style={{ whiteSpace: 'pre-wrap' }}>{v}</div>} placement="topLeft" overlayStyle={{ maxWidth: 520 }}>{v}</Tooltip>
        : <Typography.Text type="secondary">—</Typography.Text>) },
    statusColumn(CLIENT_STATUSES),
    { title: 'Campaigns', dataIndex: 'CampaignIDs', key: 'CampaignIDs', render: (ids) => tagList(ids, campaignById) },
    { title: 'Teams', dataIndex: 'TeamIDs', key: 'TeamIDs', render: (ids) => tagList(ids, teamById) },
    { title: 'Help Desk Queue', dataIndex: 'HelpDeskQueueName', key: 'HelpDeskQueueName', width: 150,
      sorter: (a, b) => byName(a.HelpDeskQueueName, b.HelpDeskQueueName), render: (v) => v || '—' },
    { title: 'Contacts', dataIndex: 'ContactIDs', key: 'ContactIDs', render: (ids) => tagList(ids, contactById) },
    commentsColumn((c) => setCommentsFor({ kind: 'clients', id: c.ID, name: c.Name, campaigns: campaignChoices(c.CampaignIDs) })),
  ];

  // -- contacts --------------------------------------------------------------

  const contactColumns = [
    { title: 'ID', dataIndex: 'ID', key: 'ID', width: 60 },
    { title: 'Name', key: 'Name', width: 180, defaultSortOrder: 'ascend',
      sorter: (a, b) => byName(`${a.LastName} ${a.FirstName}`, `${b.LastName} ${b.FirstName}`),
      render: (_, c) => <Link to={`/admin/client-contacts/${c.ID}`}>{c.FirstName} {c.LastName}</Link> },
    { title: 'Title', dataIndex: 'Title', key: 'Title', width: 160, sorter: (a, b) => byName(a.Title, b.Title) },
    statusColumn(CONTACT_STATUSES, ['Active']),
    { title: 'Email', dataIndex: 'Email', key: 'Email', width: 200,
      render: (v) => v ? <a href={`mailto:${v}`}>{v}</a> : null },
    { title: 'Phone', dataIndex: 'Phone', key: 'Phone', width: 130 },
    { title: 'Cell', dataIndex: 'Cell', key: 'Cell', width: 130 },
    { title: 'Clients', dataIndex: 'ClientIDs', key: 'ClientIDs',
      render: (ids) => (
        <Space size={[0, 4]} wrap>
          {(ids || []).map((id) => <Tag key={id}><Link to={`/admin/clients/${id}`}>{clientById[id] || `#${id}`}</Link></Tag>)}
        </Space>
      ) },
    { title: 'Campaigns', dataIndex: 'CampaignIDs', key: 'CampaignIDs', render: (ids) => tagList(ids, campaignById) },
    { title: 'Teams', dataIndex: 'TeamIDs', key: 'TeamIDs', render: (ids) => tagList(ids, teamById) },
    commentsColumn((c) => setCommentsFor({ kind: 'client-contacts', id: c.ID, name: `${c.FirstName} ${c.LastName}`, campaigns: campaignChoices(c.CampaignIDs) })),
    // No delete: a contact is retired by setting it Inactive.
    ...(!canEdit ? [] : [{ title: '', key: 'actions', width: 90, align: 'right',
      render: (_, c) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => setContactModal({ contact: c })}>Edit</Button>
      ) }]),
  ];

  // -- search ----------------------------------------------------------------

  const q = (search[tab] || '').trim().toLowerCase();
  const shownClients = !q ? clients : clients.filter((c) =>
    [c.Name, c.State, c.Country, c.ContractDetails].some((v) => (v || '').toLowerCase().includes(q)));
  const shownContacts = !q ? contacts : contacts.filter((c) =>
    [c.FirstName, c.LastName, `${c.FirstName} ${c.LastName}`, c.Title, c.Email, c.Phone, c.Cell]
      .some((v) => (v || '').toLowerCase().includes(q)));

  const toolbar = (addLabel, onAdd) => (
    <Space style={{ marginBottom: 12 }}>
      <Input.Search allowClear placeholder="Search" style={{ width: 260 }}
        value={search[tab]} onChange={(e) => setSearch({ ...search, [tab]: e.target.value })} />
      {canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>{addLabel}</Button>}
    </Space>
  );

  return (
    <div>
      <Typography.Title level={3} style={{ marginTop: 0 }}>Clients</Typography.Title>
      <Tabs activeKey={tab} onChange={setTab} items={[
        { key: 'clients', label: `Clients (${clients.length})`, children: (
          <>
            {toolbar('Add Client', openAddClient)}
            <Table rowKey="ID" size="small" loading={loading} dataSource={shownClients} columns={clientColumns}
              pagination={{ defaultPageSize: 50, hideOnSinglePage: true }} scroll={{ x: 1370 }} />
          </>
        ) },
        { key: 'contacts', label: `Contacts (${contacts.length})`, children: (
          <>
            {toolbar('Add Contact', () => setContactModal({ contact: null }))}
            <Table rowKey="ID" size="small" loading={loading} dataSource={shownContacts} columns={contactColumns}
              pagination={{ defaultPageSize: 50, hideOnSinglePage: true }} scroll={{ x: 1670 }} />
          </>
        ) },
      ]} />

      <Modal maskClosable={false} open={addClientOpen} title="Add Client"
        onCancel={() => setAddClientOpen(false)} onOk={addClient} okText="Save" confirmLoading={saving} destroyOnClose>
        <Form form={clientForm} layout="vertical" preserve={false}
          initialValues={{ status: 'Client', campaign_ids: [], team_ids: [] }}>
          <ClientFields options={options} />
        </Form>
      </Modal>

      <ContactModal
        open={!!contactModal}
        contact={contactModal?.contact}
        clients={clients}
        options={options}
        onClose={() => setContactModal(null)}
        onSaved={() => { setContactModal(null); load(); }}
      />

      <RecordCommentsDrawer record={commentsFor} canAdd={canEdit}
        onChanged={(closing) => { if (closing) setCommentsFor(null); load(); }} />
    </div>
  );
}
